import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ServiceHero from './components/ServiceHero';
import ServiceCards from './components/ServiceCards';
import OmronSection from './components/OmronSection';
import LoxoneSection from './components/LoxoneSection';
import ProjectGallery from './components/ProjectGallery';
import PartnerReferenzen from './components/PartnerReferenzen';
import LeistungenFaq from './components/LeistungenFaq';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import usePageMeta from '@/hooks/usePageMeta';

const SCHEMA_JSON = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Leistungen gowiTEC – Sondermaschinenbau & Automatisierung',
  url: 'https://gowitec.de/leistungen',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Service',
        name: 'Sondermaschinen & Anlagenbau',
        description: 'Entwicklung und Fertigung von Sondermaschinen und Anlagen nach Kundenvorgaben – von der Konzeption bis zur schlüsselfertigen Inbetriebnahme.',
        provider: { '@type': 'LocalBusiness', name: 'gowiTEC', url: 'https://gowitec.de' },
        areaServed: 'DE',
        url: 'https://gowitec.de/leistungen',
      },
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Service',
        name: 'Steuerungstechnik, SPS & Automation',
        description: 'SPS-Programmierung und Integration von Steuerungssystemen führender Hersteller: Siemens SIMATIC, Beckhoff TwinCAT, OMRON Sysmac. Visualisierung mit SCADA und HMI.',
        provider: { '@type': 'LocalBusiness', name: 'gowiTEC', url: 'https://gowitec.de' },
        areaServed: 'DE',
        url: 'https://gowitec.de/leistungen',
      },
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Service',
        name: 'Roboter- & Handlingsysteme',
        description: 'Planung und Realisierung vollautomatischer Handlingsysteme und Industrieroboter. Zertifizierter Systemintegrator für Palettier-, Montage- und Verpackungsanlagen.',
        provider: { '@type': 'LocalBusiness', name: 'gowiTEC', url: 'https://gowitec.de' },
        areaServed: 'DE',
        url: 'https://gowitec.de/leistungen',
      },
    },
    {
      '@type': 'ListItem',
      position: 4,
      item: {
        '@type': 'Service',
        name: 'Industrielle Bildverarbeitung',
        description: 'Maßgeschneiderte Vision-Systeme für Qualitätskontrolle, Fehlererkennung und Prozessüberwachung in Echtzeit. Kamera- und Beleuchtungssysteme, OCR, Barcode- und DataMatrix-Lesung.',
        provider: { '@type': 'LocalBusiness', name: 'gowiTEC', url: 'https://gowitec.de' },
        areaServed: 'DE',
        url: 'https://gowitec.de/leistungen',
      },
    },
    {
      '@type': 'ListItem',
      position: 5,
      item: {
        '@type': 'Service',
        name: 'Mechanische Fertigung',
        description: 'CNC-Fräsen, Draht- und Senkerodieren sowie Baugruppen- und Vorrichtungsfertigung. Präzisionsfertigung in Metall und Kunststoff für Klein- und Mittelserie.',
        provider: { '@type': 'LocalBusiness', name: 'gowiTEC', url: 'https://gowitec.de' },
        areaServed: 'DE',
        url: 'https://gowitec.de/leistungen',
      },
    },
    {
      '@type': 'ListItem',
      position: 6,
      item: {
        '@type': 'Service',
        name: 'Hard- & Softwareentwicklung',
        description: 'Kundenspezifische Hard- und Softwarelösungen: Schaltplanerstellung mit WSCAD, Embedded-Systeme, PC-basierte Steuerungen, Prüfstand- und Messsoftware.',
        provider: { '@type': 'LocalBusiness', name: 'gowiTEC', url: 'https://gowitec.de' },
        areaServed: 'DE',
        url: 'https://gowitec.de/leistungen',
      },
    },
    {
      '@type': 'ListItem',
      position: 7,
      item: {
        '@type': 'Service',
        name: 'Lasertechnik',
        description: 'Präzises Laserschneiden, Laserschweißen, Lasermarkierung und Lasergravur für Metall, Kunststoff und Verbundwerkstoffe – integrierbar in automatisierte Fertigungslinien.',
        provider: { '@type': 'LocalBusiness', name: 'gowiTEC', url: 'https://gowitec.de' },
        areaServed: 'DE',
        url: 'https://gowitec.de/leistungen',
      },
    },
  ],
});

const LeistungenPage = () => {
  usePageMeta({
    title: 'Leistungen – Sondermaschinenbau, SPS, OMRON & Lasertechnik | gowiTEC Reichshof',
    description: 'gowiTEC bietet Sondermaschinenbau, SPS-Steuerung (Siemens, Beckhoff, OMRON), Robotersysteme, Bildverarbeitung, mechanische Fertigung und Lasertechnik – individuelle Automatisierungslösungen seit 1992.',
    keywords: 'Sondermaschinenbau, SPS-Programmierung OMRON, Lasertechnik Reichshof, Automatisierungstechnik, Laserschneiden Maschinenbau, Steuerungstechnik NRW',
    canonical: 'https://gowitec.de/leistungen',
  });

  useEffect(() => {
    const id = 'schema-leistungen';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = SCHEMA_JSON;
    return () => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <ServiceHero />
        <ServiceCards />
        <OmronSection />
        <LoxoneSection />
        <PartnerReferenzen />
        <ProjectGallery />
        <LeistungenFaq />
        {/* CTA */}
        <section className="py-20 bg-[#1E3A8A]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Bereit für Ihr nächstes Projekt?</h2>
            <p className="text-blue-200 text-lg mb-8 max-w-xl mx-auto">
              Kontaktieren Sie uns – wir beraten Sie kostenlos und unverbindlich zu Ihrer Automatisierungsaufgabe.
            </p>
            <Link
              to="/anfrage"
              className="inline-flex items-center gap-2 bg-[#DC2626] hover:bg-[#b91c1c] text-white font-semibold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Jetzt Projekt anfragen
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LeistungenPage;
